import React from "react";
import Image from "components/image";
import { Swiper, SwiperSlide } from "components/swiper";
import { isMobile } from "utils/device-detect";
import { ReactComponent as RightArrow } from "assets/svgs/right-arrow.svg";
import { ReactComponent as LeftArrow } from "assets/svgs/left-arrow.svg";
import { ReactComponent as RightArrowInWhite } from "assets/svgs/right-arrow-in-white.svg";
import { ReactComponent as LeftArrowInWhite } from "assets/svgs/left-arrow-in-white.svg";
import { addCartItems } from "actions/cart";
import { useSelector, useDispatch } from "react-redux";
import { OPEN_CART } from "utils/action-types";
import { openKlaviyoForm } from "utils/klaviyo";
import Button from "components/button";
import { useGlobalContext } from "context/global";
import clsx from "clsx/lite";
import { trackAddToCart } from "services/analytics";
import { formatTitle } from "helpers/format-title";

const ImageSlide = ({ slide, mobileView, className = "" }) => {
	const dispatch = useDispatch();
	const { authenticated } = useSelector((state) => state.account.profile);

	const addProductsToCart = async () => {
		if (slide?.products?.length > 0) {
			const availableProducts = slide.products
				.map(({ variant }) => {
					if (["available", "one-time"].includes(variant.stock))
						return {
							id: variant.id,
							oneTime: variant.stock === "one-time",
						};

					return false;
				})
				.filter(Boolean);

			await dispatch(addCartItems(availableProducts));
			trackAddToCart(availableProducts);
			dispatch({ type: OPEN_CART });
		}
	};

	const { loggedInImage = {}, image = {} } = slide || {};

	const responsiveImage = mobileView
		? (authenticated && loggedInImage.mobileImage?.asset ? loggedInImage.mobileImage : image.mobileImage)
		: (authenticated && loggedInImage.desktopImage?.asset ? loggedInImage.desktopImage : image.desktopImage);

	const link = authenticated && loggedInImage.link ? loggedInImage.link : image.link;

	const handleOnClick = () => {
		addProductsToCart();
		openKlaviyoForm(slide?.klaviyoFormId);
	};

	return (
		<button className="h-full cursor-pointer" onClick={handleOnClick}>
			<Image
				className={clsx("size-full", className)}
				image={responsiveImage}
				alt={responsiveImage?.alt}
				to={link}
			/>
		</button>
	);
};

const ImageSlider = ({ data }) => {
	const { isSubscriptionPage } = useGlobalContext();
	const key = data?._key?.replace(/\d/g, "");
	const mobileView = isMobile();

	const settings = {
		slidesPerView: mobileView ? data?.amount?.mobile || 1 : data?.amount?.desktop || 1,
		preventClicksPropagation: false,
		mousewheel: {
			enabled: true,
			forceToAxis: true,
		},
		slidesPerGroup: 1,
		spaceBetween: 16,
		navigation: {
			enabled: true,
			nextEl: `.${key}-right-arrow`,
			prevEl: `.${key}-left-arrow`,
		},
		pagination: {
			el: ".swiper-pagination",
			clickable: true,
			type: "none",
		},
		breakpoints: {
			992: {
				slidesPerView: data?.amount?.desktop ? data?.amount?.desktop : 1,
				pagination: {
					type: "none",
				},
			},
			320: {
				pagination: {
					type: data?.showDots ? "bullets" : "none",
				},
			}
		},
		a11y: true,
		speed: 800,
	};

	if (!data.slides?.length) return null;

	return (
		<div className="pb-5 hd-screen-max-width">
			{data.title && (
				<h2
					className={clsx(
						"px-8 py-6 md:py-2 leading-[2.888rem] md:leading-[2.625rem] text-[2.1875rem]",
						data.isCenteredInDesktop && "md:text-center",
						data.isCenteredInMobile && mobileView && "text-center"
					)}
				>
					{formatTitle(data.title)}
				</h2>
			)}

			{data.subtitle && (
				<p
					className={clsx(
						"px-8",
						data.isCenteredInDesktop && "md:text-center",
						data.isCenteredInMobile && mobileView && "text-center"
					)}
				>
					{data.subtitle}
				</p>
			)}
			<div className="relative h-full pt-3 md:pt-8">
				<Swiper
					{...settings}
					className={clsx(
						"h-full",
						isSubscriptionPage ? "pb-4" : "w-11/12 mx-auto"
					)}
				>
					{data.slides.map((item, i) => (
						<SwiperSlide key={i}>
							<ImageSlide slide={item} className={clsx(!data?.showOriginalImg && "rounded-2xl aspect-square")} />
							{item?.title && <h3 className="mt-6 mb-4 pr-8 text-dark-blue">{item.title}</h3>}
							{item?.subtitle && <p className="text-lg pr-2 md:pr-4 text-dark-blue">{item.subtitle}</p>}
						</SwiperSlide>
					))}
					<div className="swiper-pagination"></div>
				</Swiper>
				{data.slides.length > 1 && (
					<div className={clsx("pt-5 md:pt-0", data?.showDots && "hidden md:block")}>
						<div className={`${key}-left-arrow left-arrow-is`}>
							{data.arrowInWhite ?
								<LeftArrowInWhite role="presentation" aria-hidden="true" /> :
								<LeftArrow role="presentation" aria-hidden="true" />}
						</div>
						<div className={`${key}-right-arrow right-arrow-is`}>
							{data.arrowInWhite ?
								<RightArrowInWhite role="presentation" aria-hidden="true" /> :
								<RightArrow role="presentation" aria-hidden="true" />}
						</div>
					</div>
				)}
			</div>
			<Button data={data.cta} className={clsx(data?.showDots ? "mt-5 lg:mt-0" : "mt-10 lg:mt-0")} />
		</div>
	);
};

export default ImageSlider;
